import React from "react";
import Lot from "../../services/Lot";
import Tracker from "../../services/Tracker";
import { motion } from "framer-motion";
import IconLive from "../../../svg/IconLive";
import { DiscountTag } from "./components/DiscountTag/DiscountTag";
import { useItemCard } from "./ItemCard.controller";
import styles from "./ItemCard.module.scss";
import classNames from "classnames";
import {getHtmlTextValue} from "../../../../shared/utils/getHtmlTextValue";
import { formatCountdown } from "../../../../shared/utils/formatCountdown";

const buyNowCTA = GlobalConfig.buyNowCTA;

class ItemCard extends React.Component {
  constructor(props) {
    super(props);
    this.trackerService = new Tracker();
    this.lotService = new Lot();

    this.onLotClicked = this.onLotClicked.bind(this);
    this.onWatchClicked = this.onWatchClicked.bind(this);

    const lot = this.props.item;
    const watched = window.lotIsWatched(lot.id);
    const { is_closing_now, is_closing_today, is_closing_soon } = this.getClosingStatus(lot);
    this.state = {
      watched,
      countdown: (is_closing_now || is_closing_today || is_closing_soon) ? formatCountdown(lot.close_time) : null,
    };
  }

  componentDidMount() {
    this.syncCountdown();
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.item;
    const next = this.props.item;
    if (prev.id !== next.id || prev.close_time !== next.close_time) {
      this.syncCountdown();
    }
  }

  componentWillUnmount() {
    this.clearCountdownTimers();
  }

  clearCountdownTimers() {
    if (this._countdownInterval) {
      clearInterval(this._countdownInterval);
      this._countdownInterval = null;
    }
    if (this._countdownEnterTimeout) {
      clearTimeout(this._countdownEnterTimeout);
      this._countdownEnterTimeout = null;
    }
  }

  // Point the countdown at whatever lot this card is currently showing. The
  // list can hand an existing card a different lot, so the timer has to read
  // from props on every tick instead of the lot captured when the card was
  // created, otherwise the card keeps counting down the previous lot.
  syncCountdown() {
    this.clearCountdownTimers();

    const lot = this.props.item;
    const { is_closing_now, is_closing_today, is_closing_soon } = this.getClosingStatus(lot);
    const in_countdown_window = is_closing_now || is_closing_today || is_closing_soon;

    this.setState({
      countdown: in_countdown_window ? formatCountdown(lot.close_time) : null,
    });

    if (in_countdown_window) {
      this._countdownInterval = setInterval(() => {
        const countdown = formatCountdown(this.props.item.close_time);
        if (countdown === null) {
          this.clearCountdownTimers();
        }
        this.setState({ countdown });
      }, 1000);
      return;
    }

    // Lot closes further out than the countdown window. Wake up once when it
    // crosses in so a page left open still starts ticking without a reload.
    const ms_until_window = this.msUntilCountdownWindow(lot);
    if (ms_until_window !== null) {
      this._countdownEnterTimeout = setTimeout(() => {
        this._countdownEnterTimeout = null;
        this.syncCountdown();
      }, ms_until_window);
    }
  }

  msUntilCountdownWindow(lot) {
    if (!lot.close_time) return null;

    const ms_remaining = lot.close_time * 1000 - Date.now();
    if (ms_remaining <= 0) return null;

    const ms_until = ms_remaining - 72 * 3600 * 1000;
    if (ms_until <= 0) return null;

    // setTimeout overflows past roughly 24.8 days, so cap it and re-check then.
    return Math.min(ms_until, 24 * 24 * 3600 * 1000);
  }

  getClosingStatus(lot) {
    const nowSeconds = Date.now() / 1000;
    const { close_time, bid_count, watch_count } = lot;
    const remaining = close_time ? close_time - nowSeconds : null;
    const open = remaining !== null && remaining > 0;
    return {
      is_closing_now:   open && remaining < 15 * 60,
      is_closing_today: open && remaining >= 15 * 60 && new Date(close_time * 1000).toDateString() === new Date().toDateString(),
      is_closing_soon:  open && remaining >= 15 * 60 && remaining < 72 * 3600 && new Date(close_time * 1000).toDateString() !== new Date().toDateString(),
      is_hidden_gem:    bid_count === 0,
      is_popular:       bid_count > 10 || watch_count > 20,
    };
  }

  onLotClicked({ event, lot, meta, url }) {
    const isFavClicked =
      $(event.target).parents(".add-to-watchlist").length > 0;
    if (isFavClicked) {
      event.preventDefault();
      return;
    }

    const v = meta.v;
    if (v == "homepage2") {
      const { pos, row_type, row_title, row_rank, lot_count } = meta;
      this.trackerService.trackLotClickV2({
        pos,
        row_type,
        row_title,
        row_rank,
        lot_count,
      });

      this.trackerService.trackLotClickUnfied({
        page_type: 'HomePage', 
        page_version: 'v2'
      })
    } else {
      const { page, pos, lot_count, keyword } = meta;
      this.trackerService.trackLotClick({ keyword, pos, lot, page, lot_count });
      this.trackerService.trackLotClickUnfied({
        page_type: keyword, 
        page_version: 'v1'
      })
    }
    // location.href = url
  }

  onWatchClicked({ event, lot }) {
    const self = this;
    if (window.CharityBuzz.is_logged_in) {
      const id = lot.id;
      const watched = this.state.watched;
      const watching = !watched;

      this.lotService
        .saveWatch({
          lot_id: id,
          watch: watching,
        })
        .done((data) => {
          if (data.watching) {
            self.setState({ watched: true });
            // const item = {
            //   item_id: id,
            //   attributes: {
            //     item_id: id,
            //     watching: true
            //   }
            // }
            window.addWatching(data);
            window.ga("send", "event", "watch", id);
            dataLayer.push({
              'event': 'ua_event',
              'event_category': 'watch',
              'event_action': id
            });
          } else {
            self.setState({ watched: false });
            window.removeWatching(id);
            window.ga("send", "event", "unwatch", id);
            dataLayer.push({
              'event': 'ua_event',
              'event_category': 'unwatch',
              'event_action': id
            });
          }

          // check notification settings
          const checkNotifSettings = new CustomEvent(
            "check_notification_settings",
            { detail: { event_action: "watch", last_action_id: data.id } }
          );
          document.dispatchEvent(checkNotifSettings);
        });
    } else {
      window.sendLoginEventToGa("Watch", lot.id);
      window.showLoginPopup();
    }
  }

  getUrlForLot({ lot }) {
    // let category = null
    // const pathName = location.pathname.toLocaleLowerCase()
    // if(pathName.startsWith("/categories/")){
    //   category = p.split("/")[2]
    // }

    // Green Room lots (DEV-3116) live on the shop.
    if (lot.url) return lot.url;

    const category = (CharityBuzz || {}).currentCategory;

    let url = `/catalog_items/${lot.pretty_id || lot.id}`;
    if (category) {
      if (category == "0") {
        // category = 'view-all'
      } else {
        url = `/categories/${category}${url}`;
      }
    }

    return url;
  }

  render() {
    const self = this;
    const lot = this.props.item;
    const meta = this.props.meta;

    const title = { __html: htmlDecode(lot.title) };
    const current_price = accounting.formatMoney(lot.current_price);
    const next_bid = accounting.formatMoney(lot.minimum_bid);
    const est_price = accounting.formatMoney(lot.estimated_price);
    const is_bin = lot.buying_format == "Buy It Now";
    //Need logic to test if a lot is CB Live
    const url = self.getUrlForLot({ lot });
    const buying_format = is_bin ? buyNowCTA : "Auction";
    const watched = this.state.watched;
    const { is_closing_now, is_closing_today, is_closing_soon, is_hidden_gem } = this.getClosingStatus(lot);

    let on_sale_details = lot.on_sale_details
    if(!on_sale_details){
      if(lot.discount_type && lot.discount_type == 'on-sale'){
        on_sale_details = {}
        on_sale_details['original_price'] = lot.discount_original_price
        on_sale_details['discount_percent'] = lot.discount_percent
        on_sale_details['discount_amount'] = lot.discount_amount
      }
    }
  

    const { renderDiscountTag,
            renderOnSaleTag,
            discountAmount,
            discountPercent,
            originalPrice } = useItemCard({
      is_bin,
      current_price: lot.current_price,
      est_price: lot.estimated_price,
      on_sale_details: on_sale_details
    });

    const { countdown } = this.state;

    let closing_badge = "";
    if (!is_bin){
      if (is_closing_now) {
        closing_badge = countdown || "";
      } else if (is_closing_today) {
        closing_badge = countdown || "";
      } else if (is_closing_soon) {
        // Show the same live countdown on closing-soon lots (24–72h
        // window) instead of the static "Closing Soon" text so the
        // urgency reads consistently across all closing windows.
        closing_badge = countdown || "Closing Soon";
      }
    }

    let buying_format_html = "";
    if (is_bin) {
      buying_format_html = (
        <span className="ico-circle c-bin">
          <i className="fal fa-tag"></i>
        </span>
      );
    }
    // Need to add the logic for this and the label text
    // Also consider if we want to check if a CB Live is currently in progress if we are going to display it when it's not yet active
    else {
      if (lot.is_live) {
        buying_format_html = (
          <span className="ico-circle c-live">
            <IconLive />
          </span>
        );
      } else {
        buying_format_html = (
          <span className="ico-circle c-bid">
            <i className="fal fa-gavel"></i>
          </span>
        );
      }
    }

    let shipping_html = "";
    if (false) {
      shipping_html = (
        <div className="meta-group__meta-data">
          <i className="fal fa-truck fa-fw"></i> Premium shipping
        </div>
      );
    }

    let dates_html = "";
    if (false) {
      dates_html = (
        <div className="meta-group__meta-data">
          <i className="fal fa-calendar-alt fa-fw"></i> Multiple dates available
        </div>
      );
    }

    let watchers_html = "";
    if (lot.watch_count > 0) {
      watchers_html = (
        <span className="tertiary-container__watchers">
          <i className="fal fa-users fa-fw"></i> &nbsp;
          {lot.watch_count} watchers
        </span>
      );
    }

    let stats_html = "";
    if (is_bin) {
      let qtyHtml = "";
      if (lot.quantity == 0) {
        qtyHtml = "Sold Out";
      } else {
        if (lot.quantity && lot.quantity <= 10) {
          qtyHtml = lot.quantity;
        } else {
          qtyHtml = "More than 10";
        }
      }

      stats_html = (
        <div
          data-title={meta.showIntro ? "ℹ️ More details! ℹ️" : null}
          data-intro={
            meta.showIntro
              ? "We've added more at-a-glance info about each item, including current bid amount or price, and value, so you have what you need instantly to take the next step."
              : null
          }
          className="card__stats-group"
        >
          <div className="stats-group__stat">&nbsp;</div>
          <div className="stats-group__stat">
            <b>Remaining:</b> {qtyHtml}
          </div>
          <div className="stats-group__stat">
            <span className={styles.label}>{renderOnSaleTag ? 'Sale' : 'Our'} Price:</span>
            <div
              className={classNames("stat__price", {
                [styles.binPriceCentered]: renderDiscountTag || renderOnSaleTag,
              })}
            >
              {current_price}

              {renderDiscountTag && (
                <span className={styles.discountPercentage}>
                  -{discountPercent}%
                </span>
              )}
            </div>
          </div>
          {renderDiscountTag && (
            <div className={styles.marketPrice}>
              <b>Market Price:</b>{" "}
              <span className={styles.lineThrough}>{est_price}</span>
            </div>
          )}
          {renderOnSaleTag && (
            <div className={styles.marketPrice}>
              <b>{buyNowCTA} Price:</b>{" "}
              <span className={styles.lineThrough}>{accounting.formatMoney(originalPrice)}</span>
              <span className={styles.discountPercentage}>
                -{discountPercent}%
              </span>
            </div>
          )}
        </div>
      );
    } else {
      stats_html = (
        <div
          data-title={meta.showIntro ? "ℹ️ More details! ℹ️" : null}
          data-intro={
            meta.showIntro
              ? "We've added more at-a-glance info about each item, including current bid amount or price, and value, so you have what you need instantly to take the next step."
              : null
          }
          className="stats-wrapper"
          className="card__stats-group"
        >
          <div className={classNames("stats-group__stat", styles.bidsWatching)}>
            <span style={{fontWeight:600}}>{lot.bid_count}</span> bids · <span style={{fontWeight:600}}>{lot.watch_count}</span> watching
          </div>
          <div className="stats-group__stat">
            <span className={styles.label}>Next Bid:</span>
            <div className="stat__price">{next_bid}</div>
          </div>
        </div>
      );
    }

    let gem_html = "";
    if (is_hidden_gem) {
      gem_html = (
        <span
          title="Hidden Gem"
          alt="Hidden Gem"
          className="optional-group__icon gem"
        >
          <i className="fal fa-gem fa-fw"></i>
        </span>
      );
    }

    const translateCard = {
      rest: {
        x: 0,
        y: 0,
      },
      hover: {
        x: 0,
        y: -4,
        transition: {
          duration: 0.4,
          delay: 0,
          delayChildren: 0,
          type: "spring",
          bounce: 0.25,
        },
      },
    };

    const scaleImage = {
      rest: { scale: 1 },
      hover: {
        scale: 1.1,
        transition: {
          duration: 0.4,
          type: "spring",
        },
      },
    };

    const lotCardHtml = (
      <motion.a
        data-title={
          meta.showIntro
            ? "👋🏻 Hi there! 👋🏻  We've made some improvements to your shopping experience."
            : null
        }
        data-intro={
          meta.showIntro
            ? "We'll quickly walk you through the updates to make sure you get the most out of your shopping experience."
            : null
        }
        aria-label={getHtmlTextValue(lot.title)}
        className="card small-12 medium-6 cell"
        onClick={(event) => self.onLotClicked({ event, lot, meta, url })}
        href={url}
        initial="rest"
        whileHover="hover"
        variants={translateCard}
      >
        <motion.figure className="card__image">
          <motion.img src={lot.lot_image} alt="" variants={scaleImage} />
          {/* The heart saves a legacy watch; Green Room lots are watched on the shop. */}
          {!lot.url && <motion.div
            className="add-to-watchlist"
            data-title={meta.showIntro ? "❤️ A new way to watch! ❤️" : null}
            data-intro={
              meta.showIntro
                ? "We've made watching any item a lot simpler. Just click or tap the heart icon and you're all set! We'll notify you about this item and any similar items we think you might like. You can always update your notification preferences in your account settings."
                : null
            }
          >
            <button
                onClick={(event) => self.onWatchClicked({ event, lot })}>
            <span
              className="ico-circle"
              ico_action="fav"
            >
              {watched ? (
                <i className="fas fa-heart watched" ico_action="fav"></i>
              ) : (
                <motion.i className="fal fa-heart" ico_action="fav"></motion.i>
              )}
            </span>
            </button>
          </motion.div>}
        </motion.figure>
        <motion.div className="card__info-container">
          <div className="info-container__label">
            <span className="label__charity">
              <span style={{display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',fontSize:'11px',color:'#595959',textTransform:'uppercase',letterSpacing:'1px',lineHeight:1.4}}>
                {lot.charity && <><span style={{fontWeight:400}}>SUPPORTS </span><span style={{fontWeight:600}}>{lot.charity}</span></>}
              </span>
            </span>
            {closing_badge ? (
              <span className="label__timer">
                <i className="fal fa-clock"></i> {closing_badge}
              </span>
            ) : null}
          </div>
          <h3
            className="card__title"
          >
            <span dangerouslySetInnerHTML={title}></span>
          </h3>
          <div className="card__meta-group">
            {shipping_html}
            {dates_html}
          </div>
          {renderDiscountTag && <DiscountTag tag_type="discount-tag" />}
          {renderOnSaleTag && <DiscountTag tag_type="on-sale-tag" />}
          {stats_html}
          <div
            data-title={meta.showIntro ? "🎉 Last one! 🎉" : null}
            data-intro={
              meta.showIntro
                ? "In this final section, you'll see how many people are watching this auction, you'll be alerted to any rewards you have to redeem, and we'll highlight if an item might be a \"hidden gem.\" We'll be launching even more exciting updates in the coming months that we hope you love! Thanks for being a Charitybuzz customer and helping to generate the most impact for cause."
                : null
            }
            className="card__tertiary-container"
          >
            {/*{watchers_html}*/}
            <span className="tertiary-container__optional-group">
              {gem_html}
              {/*
                <span
                  title="Polular Item"
                  alt="Popular Item"
                  className="optional-group__icon fire"
                >
                  <i className="fal fa-fire fa-fw"></i>
                </span>
                <span
                  title="Reward Available"
                  alt="Reward Available"
                  className="optional-group__icon gift"
                  >
                    <i className="fal fa-gift fa-fw"></i>
                </span>
              */}
            </span>
          </div>
        </motion.div>
      </motion.a>
    );

    return lotCardHtml;
  }
}

export default ItemCard;
